import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatErr } from "../lib/api";
import LegalFooter from "../components/LegalFooter";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, setSession } = useAuth();
  const [pending, setPending] = useState(null);
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(() => new URLSearchParams(window.location.search).get("suspended") ? "Your account has been suspended by admin" : "");
  const justDeleted = new URLSearchParams(window.location.search).get("deleted");
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { setErr("Please agree to the Terms & Conditions to continue"); return; }
    setErr(""); setPending(null); setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "admin" ? "/admin" : "/");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "ACCOUNT_PENDING_DELETION") setPending(d);
      else setErr(formatErr(e));
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-mesh flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo512.png" alt="Hifireels" className="w-24 h-24 mx-auto rounded-3xl object-cover mb-4 shadow-[0_0_40px_rgba(255,42,95,0.45)]"/>
          <h1 className="font-outfit font-extrabold text-4xl tracking-tighter">Welcome back</h1>
          <p className="text-white/60 mt-2">Sign in to <span className="text-gradient-cyan font-semibold">Hifireels</span></p>
        </div>
        <form onSubmit={submit} className="glass rounded-3xl p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Email or Username</label>
            <input data-testid="login-email" type="text" autoCapitalize="none" autoCorrect="off" spellCheck="false" required value={email} onChange={e=>setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 focus:border-[#00E5FF] outline-none transition" placeholder="creator001 or you@hifireels.com" />
            <p className="text-[10px] text-white/40 mt-1">You can sign in with your username (e.g. <b className="text-[#00E5FF]">creator001</b>) or your email.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Password</label>
            <div className="relative mt-1">
              <input data-testid="login-password" type={show?"text":"password"} required value={password} onChange={e=>setPassword(e.target.value)} className="w-full px-4 py-3 pr-12 rounded-2xl bg-white/5 border border-white/10 focus:border-[#00E5FF] outline-none transition" placeholder="••••••••" />
              <button type="button" onClick={()=>setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                {show?<EyeOff className="w-5 h-5"/>:<Eye className="w-5 h-5"/>}
              </button>
            </div>
          </div>
          {justDeleted && !pending && <div data-testid="login-deleted-msg" className="text-sm bg-white/5 px-3 py-2 rounded-xl border border-white/10 text-white/80">Your account has been deleted. You can recover it by logging in within 30 days.</div>}
          {pending && (
            <div className="p-3 rounded-2xl bg-[#FFD700]/10 border border-[#FFD700]/40 text-sm space-y-2" data-testid="login-pending-deletion">
              <p className="text-white/85">{pending.message}</p>
              <button type="button" data-testid="login-recover-btn" disabled={loading}
                onClick={async () => { setLoading(true); try { const { data } = await api.post("/account/recover", { identifier: email, password }); const u = setSession(data); nav(u.role === "admin" ? "/admin" : "/"); } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); } }}
                className="w-full py-2.5 rounded-full bg-[#FFD700] text-black font-extrabold disabled:opacity-50">Recover my account</button>
            </div>
          )}
          {err && <div data-testid="login-error" className="text-[#FF2A5F] text-sm bg-[#FF2A5F]/10 px-3 py-2 rounded-xl border border-[#FF2A5F]/30">{err}</div>}
          <label className="flex items-start gap-2 text-xs text-white/80 py-1" data-testid="login-agree-label">
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} data-testid="login-agree" className="mt-0.5 w-4 h-4 accent-[#00E5FF]"/>
            <span>I agree to the <Link to="/policy?tab=terms" target="_blank" className="text-[#00E5FF] underline">Terms & Conditions</Link>, <Link to="/policy?tab=privacy" target="_blank" className="text-[#00E5FF] underline">Privacy Policy</Link> and <Link to="/policy?tab=community" target="_blank" className="text-[#00E5FF] underline">Community Guidelines</Link></span>
          </label>
          <button data-testid="login-submit" disabled={loading || !agree} className="w-full py-3.5 rounded-full bg-[#00E5FF] text-black font-outfit font-bold text-base neon-cyan active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin"/>Signing in...</> : "Sign In"}
          </button>
          <div className="flex justify-between text-sm pt-2">
            <Link to="/forgot" data-testid="forgot-link" className="text-white/70 hover:text-[#00E5FF]">Forgot password?</Link>
            <Link to="/signup" data-testid="signup-link" className="text-[#00E5FF] font-semibold">Create account</Link>
          </div>
        </form>
        <LegalFooter/>
      </div>
    </div>
  );
}
